import React, { useEffect, useState } from 'react';
import { EMPTY_RECORD, useDetailRecord } from 'helpers/detailRequest';
import { confirmAlert, TwIconButton, DetailRecordFallback } from 'components/common';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  FaUserTag, FaEdit, FaTrash, FaEllipsisV,
  FaBolt, FaHistory,
} from 'react-icons/fa';
import {
  MdBusiness, MdDashboard, MdPeople,
  MdEmail, MdLanguage, MdPhone, MdLocationOn, MdReceiptLong,
} from 'react-icons/md';
import { LuClipboardCheck } from 'react-icons/lu';
import { BsCurrencyExchange } from 'react-icons/bs';
import { getCustomer, deleteCustomer } from 'redux/crm/customerSlice';
import { escapeHtml } from 'helpers/utils';
import CrmDetailLayout from 'components/app/crm/shared/CrmDetailLayout';

// Section components
import CustomerStats         from './CustomerStats';
import Overview              from './Overview';
import CustomerDetailContact from './CustomerDetailContact';
import KycCheckList          from './kyc/KycCheckList';
import OpportunityTable      from './opportunity/OpportunityTable';
import LeadTable             from './lead/LeadTable';
import CustomerActivity      from './CustomerActivity';
import AssignCustomerModal   from './AssignCustomerModal';
import EditCustomerDrawer    from '../EditCustomerDrawer';
import RecipientActivityPanel from 'components/app/crm/shared/RecipientActivityPanel';
import useBreadcrumb from 'hooks/useBreadcrumb';
import PropTypes from 'prop-types';

// ── Tabs ──────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'overview',      label: 'Overview',         Icon: MdDashboard,             accent: '#3b82f6' },
  { key: 'contacts',      label: 'Contacts',         Icon: MdPeople,                accent: '#0891b2' },
  { key: 'opportunities', label: 'Opportunities',    Icon: BsCurrencyExchange,      accent: '#10b981' },
  { key: 'leads',         label: 'Leads',            Icon: MdPeople,                accent: '#f59e0b' },
  { key: 'kyc',           label: 'KYC',              Icon: LuClipboardCheck, accent: '#7c3aed' },
  { key: 'actNotes',      label: 'Activity & Notes', Icon: FaBolt,                  accent: '#ec4899' },
  { key: 'history',       label: 'History',          Icon: FaHistory,               accent: '#64748b' },
];

// ── Actions dropdown ──────────────────────────────────────────────────────────
const ActionsMenu = ({ onAssign, onEdit, onDelete, allow_assign, allow_edit, allow_delete }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      {allow_assign && (
        <button type="button" onClick={onAssign}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-1.5 tw-rounded tw-text-xs tw-font-semibold tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-border-0 tw-cursor-pointer tw-transition-colors">
          <FaUserTag size={11} /> Assign to
        </button>
      )}
      {(allow_edit || allow_delete) && (
        <div className="tw-relative">
          <TwIconButton icon={FaEllipsisV} iconSize={12} variant="secondary" size="lg" onClick={() => setOpen(v => !v)} title="More actions" />
          {open && (
            <>
              <div className="tw-fixed tw-inset-0 tw-z-40" onClick={() => setOpen(false)} />
              <div className="tw-absolute tw-right-0 tw-top-10 tw-z-50 tw-min-w-[140px] tw-bg-white tw-rounded-lg tw-shadow-xl tw-border tw-border-slate-200 tw-py-1.5">
                {allow_edit && (
                  <button type="button" onClick={() => { setOpen(false); onEdit(); }}
                    className="tw-flex tw-items-center tw-gap-2.5 tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-blue-600 hover:tw-bg-blue-50 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-left">
                    <FaEdit size={12} /> Edit
                  </button>
                )}
                {allow_edit && allow_delete && <div className="tw-border-t tw-border-slate-100 tw-mx-2 tw-my-1" />}
                {allow_delete && (
                  <button type="button" onClick={() => { setOpen(false); onDelete(); }}
                    className="tw-flex tw-items-center tw-gap-2.5 tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 hover:tw-bg-red-50 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-left">
                    <FaTrash size={12} /> Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

ActionsMenu.propTypes = {
  allow_assign: PropTypes.bool,
  allow_edit: PropTypes.bool,
  allow_delete: PropTypes.bool,
  onAssign: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func
};

// ── CustomerDetail ────────────────────────────────────────────────────────────
const CustomerDetail = () => {
  const params   = useParams();
  // Only the record in the URL — the persisted store can still hold the one
  // viewed last (e.g. when opened from an email link).
  const { record, waiting, error } = useDetailRecord('crm_customer', params.id);
  const customer = record || EMPTY_RECORD;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Breadcrumb: hiển thị tên customer thay vì id
  useBreadcrumb(
    customer
      ? [
          { label: 'Customers', to: '/crm/customers/list' },
          { label: customer.name || `Customer ${params.id}` }
        ]
      : null
  );

  const [activeTab,       setActiveTab]       = useState('overview');
  const [mountedTabs,     setMountedTabs]     = useState(new Set(['overview']));
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditDrawer,  setShowEditDrawer]  = useState(false);

  useEffect(() => { dispatch(getCustomer(params.id)); }, [params.id, dispatch]);

  const switchTab = (key) => {
    setActiveTab(key);
    setMountedTabs(prev => new Set([...prev, key]));
  };

  const { allow_edit, allow_delete, allow_assign, allow_create_contact } =

    customer?.permissions || {};

  const handleDelete = () =>
    confirmAlert({
      title: 'Delete customer',
      message: `Delete customer "<strong>${escapeHtml(customer?.name)}</strong>"? This cannot be undone.`,
      type: 'danger',
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Delete', variant: 'danger',
          onClick: async () => {
            try { dispatch(deleteCustomer(customer.id)); navigate('/crm/customers/list'); }
            catch (e) { toast.error(e.message); }
          },
        },
        { label: 'Cancel', onClick: () => {} },
      ],
    });

  // Badge colours per type
  const typeColor = {
    'End-user':   'tw-bg-sky-50 tw-text-sky-700 tw-border-sky-200',
    'Contractor': 'tw-bg-orange-50 tw-text-orange-700 tw-border-orange-200',
    'Consultant': 'tw-bg-violet-50 tw-text-violet-700 tw-border-violet-200',
  };

  if (!record) return <DetailRecordFallback waiting={waiting} title="Customer not available" error={error} onRetry={() => dispatch(getCustomer(params.id))} />;

  return (
    <>
      <CrmDetailLayout
        avatar={
          customer?.logo_url ? (
            <img src={customer.logo_url} alt={customer?.name || 'Customer'}
              className="tw-w-full tw-h-full tw-object-contain tw-p-1" />
          ) : (
            <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-slate-100 tw-text-slate-400">
              <MdBusiness size={28} />
            </div>
          )
        }
        title={customer?.name}
        badges={[
          customer?.id && (
            <span key="id" className="tw-text-[11px] tw-font-mono tw-text-slate-400 tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-rounded-md">
              ID#{customer.id}
            </span>
          ),
          customer?.type_of_customer && (
            <span key="type" className={`tw-text-[11px] tw-font-semibold tw-border tw-px-2.5 tw-py-0.5 tw-rounded-xl ${typeColor[customer.type_of_customer] || 'tw-bg-slate-100 tw-text-slate-600 tw-border-slate-200'}`}>
              {customer.type_of_customer}
            </span>
          ),
        ].filter(Boolean)}
        chips={(() => {
          // Filter helper: skip null, undefined, empty string, and the literal string "undefined"
          const valid = (v) => v != null && v !== '' && v !== 'undefined' && v !== 'null';
          return [
            valid(customer?.industry)    && { icon: MdBusiness,   label: customer.industry },
            valid(customer?.country)     && { icon: MdLocationOn, label: customer.country },
            valid(customer?.email)       && { icon: MdEmail,      label: customer.email,    href: `mailto:${customer.email}` },
            valid(customer?.telephone)   && { icon: MdPhone,      label: customer.telephone },
            valid(customer?.website)     && { icon: MdLanguage,   label: customer.website,  href: customer.website },
            valid(customer?.tax_code)    && { icon: MdReceiptLong, label: `Tax: ${customer.tax_code}` },
            valid(customer?.customer_primary_address) && { icon: MdLocationOn, label: customer.customer_primary_address },
          ].filter(Boolean);
        })()}
        actions={
          <ActionsMenu
            allow_assign={allow_assign}
            allow_edit={allow_edit}
            allow_delete={allow_delete}
            onAssign={() => setShowAssignModal(true)}
            onEdit={() => setShowEditDrawer(true)}
            onDelete={handleDelete}
          />
        }
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={switchTab}
      >
        {/* Overview */}
        {mountedTabs.has('overview') && (
          <div hidden={activeTab !== 'overview'} className="tw-flex tw-flex-col tw-gap-4">
            <CustomerStats projects={customer?.projects || []} />
            <Overview customer={customer} />
          </div>
        )}

        {/* Contacts */}
        {mountedTabs.has('contacts') && (
          <div hidden={activeTab !== 'contacts'}>
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm">
              <CustomerDetailContact customer={customer} allow_create={allow_create_contact} />
            </div>
          </div>
        )}

        {/* Opportunities */}
        {mountedTabs.has('opportunities') && (
          <div hidden={activeTab !== 'opportunities'}>
            <OpportunityTable data={customer?.projects || []} customer={customer} />
          </div>
        )}

        {/* Leads */}
        {mountedTabs.has('leads') && (
          <div hidden={activeTab !== 'leads'}>
            <LeadTable data={customer?.leads || []} customer={customer} />
          </div>
        )}

        {/* KYC */}
        {mountedTabs.has('kyc') && (
          <div hidden={activeTab !== 'kyc'}>
            <KycCheckList customer={customer} />
          </div>
        )}


        {/* Activity & Notes */}
        {mountedTabs.has('actNotes') && (
          <div hidden={activeTab !== 'actNotes'}>
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-p-5">
              {customer?.id && (
                <RecipientActivityPanel recipientType="company" recipientId={customer.id} />
              )}
            </div>
          </div>
        )}

        {/* History */}
        {mountedTabs.has('history') && (
          <div hidden={activeTab !== 'history'}>
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-p-5">
              <CustomerActivity />
            </div>
          </div>
        )}
      </CrmDetailLayout>

      <AssignCustomerModal
        showModal={showAssignModal}
        setshowModal={setShowAssignModal}
        customerId={customer?.id}
      />

      <EditCustomerDrawer
        show={showEditDrawer}
        onHide={() => setShowEditDrawer(false)}
        customer={customer}
        onSaved={() => dispatch(getCustomer(params.id))}
      />
    </>
  );
};

export default CustomerDetail;
